import { Fragment } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Separator } from '@givebutter/primitives/separator';
import { fetchAccountAddresses } from 'api/account';
import { PrimaryBadge } from 'assets/js/pages/contacts/PrimaryBadge';
import * as AddressManagement from 'components/address-form/AddressManagement';
import { useAccountAddressForm } from 'components/address-form/useAccountAddressForm';
import * as DropdownMenu from 'components/dropdown-menu';
import { AddressType } from 'constants/addresses';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteAccountAddresses } from 'customHooks/useInfiniteAccountAddresses';
import { accountQueryKeys } from 'queries/account-query-keys';

const SettingsAddressForm = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const match = useRouteMatch();
    const { deleteAccountAddressMutation, setPrimaryAddressMutation } = useAccountAddressForm();
    const { mutate: deleteAddress, isPending: isDeleting } = deleteAccountAddressMutation;
    const { mutate: makePrimary, isPending: isMakingPrimary } = setPrimaryAddressMutation;
    const { data: orgAddressQuery } = useSuspenseQuery({
        queryKey: accountQueryKeys.addressesWithFilters(accountId, {
            not_type: AddressType['Mailing'],
        }),
        queryFn: () => fetchAccountAddresses(accountId, { not_type: AddressType['Mailing'] }),
    });
    const orgAddress = orgAddressQuery.data[0];
    const { ref, data } = useInfiniteAccountAddresses({ type: AddressType['Mailing'] });
    const addresses = data?.pages ?? [];

    return (
        <div className="flex flex-col gap-6">
            <AddressManagement.Root className="px-6 py-4">
                <AddressManagement.Row>
                    <AddressManagement.Title>Organization address</AddressManagement.Title>
                    {!orgAddress && <AddressManagement.Button />}
                </AddressManagement.Row>
                <p className={'text-sm text-gray-600'}>
                    This address is your organization's official legal address on record.
                </p>
                <Separator className={'-mx-8 !w-auto'} />
                <div className={'flex flex-col gap-3'}>
                    {orgAddress ? (
                        <AddressManagement.Row>
                            <AddressManagement.RowDetails>
                                {orgAddress.name}
                                <AddressManagement.InlineAddressRoot address={orgAddress}>
                                    <AddressManagement.InlineAddressCopyButton />
                                </AddressManagement.InlineAddressRoot>
                            </AddressManagement.RowDetails>
                            <DropdownMenu.Root>
                                <DropdownMenu.EllipsisTrigger className="px-2" />
                                <DropdownMenu.Content align={'end'}>
                                    <DropdownMenu.Item
                                        onClick={() =>
                                            history.push(`${match.url}/${orgAddress.id}/edit`)
                                        }
                                    >
                                        Edit
                                    </DropdownMenu.Item>
                                    <AlertDialog.Root type={'error'}>
                                        <AlertDialog.Trigger asChild={true}>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                Delete
                                            </DropdownMenu.Item>
                                        </AlertDialog.Trigger>
                                        <AlertDialog.Portal>
                                            <AlertDialog.Overlay />
                                            <AlertDialog.Content>
                                                <AlertDialog.Header>
                                                    <AlertDialog.Icon />
                                                    <AlertDialog.Close />
                                                </AlertDialog.Header>
                                                <AlertDialog.Body>
                                                    <AlertDialog.Title>
                                                        Delete address
                                                    </AlertDialog.Title>
                                                    <AlertDialog.Description>
                                                        You won't be able to undo this action. Are
                                                        you sure?
                                                    </AlertDialog.Description>
                                                </AlertDialog.Body>
                                                <AlertDialog.Footer>
                                                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                    <AlertDialog.Action
                                                        disabled={isDeleting}
                                                        onClick={() => deleteAddress(orgAddress.id)}
                                                    >
                                                        Delete Address
                                                    </AlertDialog.Action>
                                                </AlertDialog.Footer>
                                            </AlertDialog.Content>
                                        </AlertDialog.Portal>
                                    </AlertDialog.Root>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </AddressManagement.Row>
                    ) : (
                        <p className={'text-center text-gray-600'}>No address found</p>
                    )}
                </div>
            </AddressManagement.Root>

            <AddressManagement.Root className="px-6 py-4">
                <AddressManagement.Row>
                    <AddressManagement.Title>Mailing and return address</AddressManagement.Title>
                    <AddressManagement.Button
                        onClick={() => {
                            history.push(`${match.url}/new/mailing`);
                        }}
                    />
                </AddressManagement.Row>
                <p className={'text-sm text-gray-600'}>
                    Addresses can be saved and used for communicating with supporters, including
                    return addresses on letters, and emails.
                </p>
                <Separator className={'-mx-8 !w-auto'} />
                <div className={'flex flex-col gap-3'}>
                    {addresses && addresses.length > 0 ? (
                        addresses.map((address, index) => (
                            <Fragment key={address.id}>
                                {index > 0 && <Separator className={'-mx-8 !w-auto'} />}
                                <AddressManagement.Row>
                                    <AddressManagement.RowDetails>
                                        {address.name}
                                        <AddressManagement.InlineAddressRoot address={address}>
                                            <AddressManagement.InlineAddressCopyButton />
                                            {address.is_primary && <PrimaryBadge />}
                                        </AddressManagement.InlineAddressRoot>
                                    </AddressManagement.RowDetails>
                                    <DropdownMenu.Root>
                                        <DropdownMenu.EllipsisTrigger className="px-2" />
                                        <DropdownMenu.Content align={'end'}>
                                            {!address.is_primary && (
                                                <DropdownMenu.Item
                                                    disabled={isMakingPrimary}
                                                    onClick={() =>
                                                        makePrimary({
                                                            addressId: address.id,
                                                            isPrimary: true,
                                                        })
                                                    }
                                                >
                                                    Make Primary
                                                </DropdownMenu.Item>
                                            )}
                                            {address.is_primary && (
                                                <DropdownMenu.Item
                                                    disabled={isMakingPrimary}
                                                    onClick={() =>
                                                        makePrimary({
                                                            addressId: address.id,
                                                            isPrimary: false,
                                                        })
                                                    }
                                                >
                                                    Unset Primary
                                                </DropdownMenu.Item>
                                            )}
                                            <DropdownMenu.Item
                                                onClick={() =>
                                                    history.push(
                                                        `${match.url}/${address.id}/edit/mailing`,
                                                    )
                                                }
                                            >
                                                Edit
                                            </DropdownMenu.Item>
                                            <AlertDialog.Root type={'error'}>
                                                <AlertDialog.Trigger asChild={true}>
                                                    <DropdownMenu.Item
                                                        onSelect={e => e.preventDefault()}
                                                    >
                                                        Delete
                                                    </DropdownMenu.Item>
                                                </AlertDialog.Trigger>
                                                <AlertDialog.Portal>
                                                    <AlertDialog.Overlay />
                                                    <AlertDialog.Content>
                                                        <AlertDialog.Header>
                                                            <AlertDialog.Icon />
                                                            <AlertDialog.Close />
                                                        </AlertDialog.Header>
                                                        <AlertDialog.Body>
                                                            <AlertDialog.Title>
                                                                Delete address
                                                            </AlertDialog.Title>
                                                            <AlertDialog.Description>
                                                                You won't be able to undo this
                                                                action. Are you sure?
                                                            </AlertDialog.Description>
                                                        </AlertDialog.Body>
                                                        <AlertDialog.Footer>
                                                            <AlertDialog.Cancel>
                                                                Cancel
                                                            </AlertDialog.Cancel>
                                                            <AlertDialog.Action
                                                                disabled={isDeleting}
                                                                onClick={() =>
                                                                    deleteAddress(address.id)
                                                                }
                                                            >
                                                                Delete Address
                                                            </AlertDialog.Action>
                                                        </AlertDialog.Footer>
                                                    </AlertDialog.Content>
                                                </AlertDialog.Portal>
                                            </AlertDialog.Root>
                                        </DropdownMenu.Content>
                                    </DropdownMenu.Root>
                                </AddressManagement.Row>
                            </Fragment>
                        ))
                    ) : (
                        <p className={'text-center text-gray-600'}>No addresses found</p>
                    )}
                </div>
            </AddressManagement.Root>
            <div ref={ref} />
        </div>
    );
};

export { SettingsAddressForm };
